import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MenuIcon, XIcon, ClipboardListIcon } from "lucide-react";
import { Logo } from "./Logo";
import { mainNav } from "../data/navigation";
import { useQuote } from "../contexts/QuoteContext";
import { useGoToQuote } from "../hooks/useGoToQuote";
export function Header({
  overHero = false
}: {
  overHero?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    items,
    open
  } = useQuote();
  const goToQuote = useGoToQuote();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const solid = !overHero || scrolled || menuOpen;
  const light = !solid;
  return <header className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color] duration-200 ease-out ${solid ? "border-b border-line bg-paper/95 backdrop-blur" : "border-b border-transparent bg-transparent"}`} data-id="element-31" data-mp="3f9ng26gmyupw">
      <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between gap-6 px-5 md:px-10" data-id="element-32" data-mp="2mnpzgrx9hjof">
        <Logo light={light} data-id="element-33" data-mp="2hon27tniwhr0" />

        <nav aria-label="Navigation principale" className="hidden xl:block" data-id="element-34" data-mp="2hpoh697ntoe0">
          <ul className="flex items-center gap-8" data-id="element-35" data-mp="3fa12084fhunv">
            {mainNav.map(item => <li key={item.to} data-id="element-36" data-mp="3adkar51xn06k">
                <NavLink to={item.to} className={`whitespace-nowrap text-sm transition-colors duration-150 ease-out ${light ? "text-paper/85 hover:text-paper" : "text-olive hover:text-terracotta"}`} data-id="element-37" data-mp="3aec5kdnpgtvs">
                  {item.label}
                </NavLink>
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-2" data-id="element-38" data-mp="3t9adiuhow0li">
          <button type="button" onClick={open} className={`relative flex h-11 items-center gap-2 rounded-full px-4 text-sm transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${light ? "text-paper hover:bg-paper/10" : "text-ink hover:bg-cream"}`} aria-label={`Ma sélection de devis, ${items.length} ingrédient${items.length > 1 ? "s" : ""}`} data-id="element-39" data-mp="1remhitbgyikx">
            <ClipboardListIcon className="h-[18px] w-[18px]" aria-hidden data-id="element-40" data-mp="1cox7s9pb9691" />
            <span className="hidden sm:inline whitespace-nowrap" data-id="element-41" data-mp="yp5q9yzl57qa">Ma sélection</span>
            <AnimatePresence initial={false} data-id="element-42" data-mp="13nz45sisb0qz">
              {items.length > 0 && <motion.span key={items.length} initial={{
              scale: 0.96,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.16,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1.5 text-[11px] font-semibold text-paper" data-id="element-43" data-mp="13q37o9crtxqq">
                  {items.length}
                </motion.span>}
            </AnimatePresence>
          </button>

          <button type="button" onClick={goToQuote} className={`hidden h-11 items-center whitespace-nowrap rounded-full px-5 text-sm font-medium transition-colors duration-150 ease-out md:flex focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 ${light ? "bg-paper text-ink hover:bg-cream" : "bg-terracotta text-paper hover:bg-terracotta-dark"}`} data-id="element-44" data-mp="rc6eguk7hstf">
            Demander un devis
          </button>

          <button type="button" onClick={() => setMenuOpen(v => !v)} className={`flex h-11 w-11 items-center justify-center rounded-full xl:hidden ${light ? "text-paper hover:bg-paper/10" : "text-ink hover:bg-cream"}`} aria-expanded={menuOpen} aria-controls="mobile-menu" aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"} data-id="element-45" data-mp="3gwt3wixu7w14">
            {menuOpen ? <XIcon className="h-5 w-5" data-id="element-46" data-mp="2gxev6w9ffuwm" /> : <MenuIcon className="h-5 w-5" data-id="element-47" data-mp="2gwc8l0j50emi" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-48" data-mp="1ka0k7emjkq5z">
        {menuOpen && <motion.nav id="mobile-menu" aria-label="Navigation mobile" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="border-t border-line bg-paper xl:hidden" data-id="element-49" data-mp="1p6qypxip7dpa">
            <ul className="mx-auto max-w-[1400px] px-5 py-4 md:px-10" data-id="element-50" data-mp="11fvbldp6t6od">
              {mainNav.map(item => <li key={item.to} data-id="element-51" data-mp="xzs3edoxs8yx">
                  <NavLink to={item.to} onClick={() => setMenuOpen(false)} className="block border-b border-line py-4 font-display text-2xl text-ink" data-id="element-52" data-mp="xypeucf36im6">
                    {item.label}
                  </NavLink>
                </li>)}
              <li className="pt-5 md:hidden" data-id="element-53" data-mp="jzhgflqqzej1">
                <button type="button" onClick={() => {
              setMenuOpen(false);
              goToQuote();
            }} className="h-12 w-full rounded-full bg-terracotta text-sm font-medium text-paper" data-id="element-54" data-mp="f0mmpor8s5k5">
                  Demander un devis
                </button>
              </li>
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}